import AddIcon from "@mui/icons-material/Add";
import { Button, IconButton, Tooltip } from "@mui/material";
import { MerchantLink } from "Components/Admin/Terminals/MerchantLink";
import {
  TerminalStatusChip,
  useTerminalStatusLabels,
} from "Components/Admin/Terminals/TerminalStatusChip";
import {
  TerminalFinancing,
  TerminalInventoryStatus,
  type TerminalInventoryListItemDto,
} from "Infrastructure/Api/Api";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_TableOptions,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo } from "react";
import { Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";
import { useTableLocalization } from "Utils/TableLocalizationUtils";

const RESOURCES = Resources.Admin.Terminals;

type Props = {
  data: Array<TerminalInventoryListItemDto>;
  isLoading: boolean;
  isEditingEnabled: boolean;
  onEdit: (terminal: TerminalInventoryListItemDto) => void;
  onAdd?: () => void;
  onShip?: (terminal: TerminalInventoryListItemDto) => void;
  isShipPending?: boolean;
  rowCount: number;
  tableProps: Partial<MRT_TableOptions<TerminalInventoryListItemDto>>;
};

// Shipping is a Mo.one back-office step — hunter-financed pieces are delivered by the hunter.
const getIsShippable = (terminal: TerminalInventoryListItemDto) =>
  terminal.status === TerminalInventoryStatus.AssignedToMerchant &&
  terminal.financing === TerminalFinancing.Moone;

export const TerminalListTable = ({
  data,
  isLoading,
  isEditingEnabled,
  onEdit,
  onAdd,
  onShip,
  isShipPending = false,
  rowCount,
  tableProps,
}: Props) => {
  const { t } = useResource();
  const localization = useTableLocalization();
  const statusLabels = useTerminalStatusLabels();

  const columns = useMemo<Array<MRT_ColumnDef<TerminalInventoryListItemDto>>>(
    () => [
      {
        accessorKey: "serialNumber",
        header: t(RESOURCES.Table.SerialNumber),
        size: 160,
      },
      {
        accessorKey: "tid",
        header: t(RESOURCES.Table.Tid),
        size: 120,
        enableSorting: false,
      },
      {
        accessorKey: "comgateMid",
        header: t(RESOURCES.Table.Mid),
        size: 120,
        enableSorting: false,
      },
      {
        accessorKey: "hardwareModel",
        header: t(RESOURCES.Table.HardwareModel),
        size: 130,
      },
      {
        accessorKey: "status",
        header: t(RESOURCES.Table.Status),
        size: 150,
        filterVariant: "select",
        filterSelectOptions: Object.values(TerminalInventoryStatus).map(
          (status) => ({
            label: statusLabels[status],
            value: status,
          }),
        ),
        Cell: ({ row }) => <TerminalStatusChip status={row.original.status} />,
      },
      {
        accessorKey: "financing",
        header: t(RESOURCES.Table.Financing),
        size: 130,
        filterVariant: "select",
        filterSelectOptions: [
          {
            label: t(RESOURCES.Financing.Moone),
            value: TerminalFinancing.Moone,
          },
          {
            label: t(RESOURCES.Financing.Hunter),
            value: TerminalFinancing.Hunter,
          },
        ],
        Cell: ({ row }) =>
          row.original.financing === TerminalFinancing.Hunter
            ? t(RESOURCES.Financing.Hunter)
            : t(RESOURCES.Financing.Moone),
      },
      {
        accessorKey: "hunterName",
        header: t(RESOURCES.Table.Hunter),
        size: 150,
        enableSorting: false,
      },
      {
        accessorKey: "merchantName",
        header: t(RESOURCES.Table.Merchant),
        size: 180,
        Cell: ({ row }) => (
          <MerchantLink
            name={row.original.merchantName}
            publicID={row.original.merchantPublicID}
            // Keep the row's edit-dialog click from firing under the navigation.
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      {
        accessorKey: "facilityName",
        header: t(RESOURCES.Table.Facility),
        size: 150,
        enableSorting: false,
      },
      {
        accessorKey: "lastChangedAt",
        header: t(RESOURCES.Table.LastChangedAt),
        size: 150,
        enableColumnFilter: false,
        Cell: ({ row }) => formatDate(row.original.lastChangedAt),
      },
    ],
    [t, statusLabels],
  );

  const table = useMaterialReactTable({
    ...tableProps,
    data,
    columns,
    rowCount,
    localization,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    enableRowActions: !!onShip,
    positionActionsColumn: "last",
    displayColumnDefOptions: {
      "mrt-row-actions": { header: "", size: 110 },
    },
    renderRowActions: onShip
      ? ({ row }) => {
          if (!getIsShippable(row.original)) {
            return null;
          }
          return (
            <Button
              size="small"
              variant="outlined"
              disabled={isShipPending}
              onClick={(e) => {
                e.stopPropagation();
                onShip(row.original);
              }}
            >
              {t(RESOURCES.Table.Ship)}
            </Button>
          );
        }
      : undefined,
    initialState: {
      ...tableProps.initialState,
      density: "compact",
      showColumnFilters: true,
    },
    renderTopToolbarCustomActions: onAdd
      ? () => (
          <Tooltip title={t(RESOURCES.Form.AddTitle)}>
            <IconButton color="primary" onClick={onAdd}>
              <AddIcon />
            </IconButton>
          </Tooltip>
        )
      : undefined,
    state: {
      ...tableProps.state,
      isLoading,
    },
    muiTableBodyRowProps: isEditingEnabled
      ? ({ row }) => ({
          onClick: () => onEdit(row.original),
          sx: { cursor: "pointer" },
        })
      : undefined,
    muiTablePaperProps: {
      sx: { borderRadius: "10px" },
    },
  });

  return <MaterialReactTable table={table} />;
};
